import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { SiteNav } from "../components/SiteNav";
import { SiteFooter } from "../components/SiteFooter";
import { APP_VERSION } from "../version";
import { CHANGELOG_FALLBACK } from "../changelogData";
import { RelayLoader } from "../components/RelayLoader";

const API = process.env.REACT_APP_BACKEND_URL;
const C = { bg: "#FAF7F0", ink: "#2A1E5C", ink2: "#5D5A6B", muted: "#8B7E6A", line: "#E8E2D5", gold: "#F5CB5C", teal: "#2E86AB" };

export default function WhatsNewPage() {
  const [changelog, setChangelog] = useState(null);
  const [version, setVersion] = useState(APP_VERSION);
  const [expanded, setExpanded] = useState(() => new Set());
  useEffect(() => {
    let cancelled = false;
    const attempt = async (retriesLeft) => {
      try {
        const r = await fetch(`${API}/api/whats-new`);
        const d = r.ok ? await r.json() : null;
        if (!(d && d.changelog && d.changelog.length)) throw new Error("empty changelog");
        if (!cancelled) {
          setChangelog(d.changelog);
          if (d.version) setVersion(d.version);
          // Auto-expand the most recent release so the page isn't blank on load
          setExpanded(new Set([d.changelog[0].version]));
        }
      } catch {
        if (cancelled) return;
        if (retriesLeft > 0) setTimeout(() => attempt(retriesLeft - 1), 1500);
        else {
          setChangelog(CHANGELOG_FALLBACK);
          setVersion(CHANGELOG_FALLBACK[0].version);
          setExpanded(new Set([CHANGELOG_FALLBACK[0].version]));
        }
      }
    };
    attempt(1);
    return () => { cancelled = true; };
  }, []);

  const toggle = (v) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(v)) next.delete(v); else next.add(v);
      return next;
    });
  };

  return (
    <div className="min-h-screen" style={{ background: C.bg }} data-testid="whatsnew-page">
      <SiteNav />
      <div className="max-w-2xl mx-auto px-5 pt-10 sm:pt-14 pb-8 sm:pb-12">
        <h1 className="text-4xl sm:text-5xl mb-8" style={{ fontFamily: "'Fraunces', Georgia, serif", color: C.ink }}>
          What's New
        </h1>

        {!changelog ? (
          <div data-testid="whatsnew-loading"><RelayLoader size="md" label="Loading the latest…" /></div>
        ) : (
          <div className="space-y-3">
            {changelog.map((entry) => {
              const isOpen = expanded.has(entry.version);
              return (
                <section
                  key={entry.version}
                  className="rounded-2xl border overflow-hidden"
                  style={{ background: "#FFFFFF", borderColor: C.line }}
                  data-testid={`whatsnew-entry-${entry.version}`}
                >
                  <button
                    type="button"
                    onClick={() => toggle(entry.version)}
                    aria-expanded={isOpen}
                    className="w-full text-left px-4 py-4 transition-colors hover:bg-black/[.02]"
                    data-testid={`whatsnew-toggle-${entry.version}`}
                  >
                    <div className="flex items-center gap-3 mb-1">
                      <span
                        className="text-xs font-bold px-3 py-1 rounded-full"
                        style={{ background: C.ink, color: "#fff" }}
                        data-testid={`whatsnew-version-${entry.version}`}
                      >
                        {entry.version}
                      </span>
                      <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: C.muted }}>{entry.date}</span>
                      <span className="ml-auto text-xs font-bold" style={{ color: C.muted }} aria-hidden="true">
                        {isOpen ? "▲" : "▼"}
                      </span>
                    </div>
                    <h2 className="text-base sm:text-lg font-bold pr-6" style={{ fontFamily: "'Fraunces', Georgia, serif", color: C.ink }}>
                      {entry.title}
                    </h2>
                    {!isOpen && (
                      <p className="text-[11px] mt-1 font-semibold" style={{ color: C.teal }}>
                        {entry.notes.length} update{entry.notes.length === 1 ? "" : "s"}. Tap to expand
                      </p>
                    )}
                  </button>
                  {isOpen && (
                    <div
                      className="px-4 pb-4 pt-1 space-y-3"
                      data-testid={`whatsnew-content-${entry.version}`}
                    >
                      {entry.notes.map((n) => (
                        <div
                          key={n.heading}
                          className="rounded-xl p-3.5 border"
                          style={{ background: C.bg, borderColor: C.line }}
                        >
                          <p className="text-sm font-bold mb-1" style={{ color: C.ink }}>{n.heading}</p>
                          <p className="text-sm leading-relaxed" style={{ color: C.ink2 }}>{n.body}</p>
                          {n.link && (
                            <Link
                              to={n.link.to}
                              className="inline-block mt-2 text-sm font-semibold underline"
                              style={{ color: C.ink }}
                              data-testid={`whatsnew-note-link-${entry.version}`}
                            >
                              {n.link.label}
                            </Link>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              );
            })}
          </div>
        )}

        <p className="text-xs text-center mt-12 leading-relaxed" style={{ color: C.muted }}>
          Spotted a teal <b style={{ color: C.teal }}>NEW</b> badge in your dashboard menu? That's a fresh feature waiting for its first click.
        </p>
        <p className="text-[11px] text-center mt-6 leading-relaxed" style={{ color: C.muted }}>
          Payday Relay is an educational tool. Not financial, tax, or investment advice.{" "}
          <Link to="/terms" className="underline">Terms</Link>
        </p>
      </div>
      <SiteFooter />
    </div>
  );
}
